import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API } from '../config/api';
import { parseAsUTC } from '../utils/timezone';

const useClassroomStore = create(
  persist(
    (set, get) => ({
      inClass: false,
      currentLessonForClass: null,
      showCameraPreview: false,
      pendingLesson: null,
      blurEnabled: false,
      // User's camera/mic preference set in CameraPreviewModal; VideoRoom reads
      // these on connect so devices toggled OFF in the preview stay OFF in class
      // (previously VideoRoomInner always acquired both, so they flipped on ~1s
      // after entering).
      initialCameraEnabled: true,
      initialMicEnabled: true,
      currentTime: new Date(),

      // Auto-end state (fires when scheduled_end + 5min → warning, +10min → end)
      autoEndWarningActive: false,
      autoEndRemainingSec: 0,

      // Timer state
      timerActive: false,
      timerEndTime: null,
      timerDuration: 0,
      timerRemainingSeconds: 0,
      timerJustEnded: false,
      timerPollingInterval: null,
      // Bumped on every local startTimer so fetchTimer can drop stale
      // "no timer" polls that raced the start request.
      _timerLastStartedAt: 0,
      // Monotonic anchor for the local countdown. `_timerAnchorPerfMs` is a
      // performance.now() timestamp captured when we last received an
      // authoritative remaining from the server; `_timerAnchorRemainingSec`
      // is that remaining value. The local tick computes remaining from these
      // two, NOT from Date.now(), so the display is immune to system-clock
      // skew or NTP adjustments on the teacher's / student's machine.
      _timerAnchorPerfMs: 0,
      _timerAnchorRemainingSec: 0,

      // Presentation state (synced across teacher and students)
      presentationShared: false,
      syncedSlideIndex: 0,
      syncedPresentationId: null,
      presentationPollingInterval: null,

      // Participants state (online presence)
      participants: [],
      participantsLoading: false,
      presencePollingInterval: null,

      // Student answers state (synced across teacher and students)
      studentAnswersMap: {}, // { participantId: { studentName, answers, slideIndex, updatedAt } }
      answersPollingInterval: null,

      // Student exercise answers state (synced - teacher sees students' quiz selections and results)
      // { studentId: { studentName, updatedAt, [elementId]: { selectedAnswers, isCorrect, attemptNumber, answeredAt } } }
      studentExerciseAnswersMap: {},
      exerciseAnswersPollingInterval: null,

      // Teacher answers state (synced to students when teacher edits editable elements)
      teacherAnswersMap: {}, // { answers: {elementId: value}, slideIndex, updatedAt }
      teacherAnswersPollingInterval: null,

      // Element positions state (synced across teacher and students)
      // Now includes per-element metadata: { elementId: { x, y, updatedAt, updatedBy } }
      syncedElementPositions: {},
      positionsPollingInterval: null,
      // Track which elements the current user has dragged locally (for conflict resolution)
      localDragTimestamps: {}, // { elementId: timestamp }

      // Interactive elements state (dice, wheel, flip cards, input fields)
      // { elementId: { elementType, state, updatedAt, updatedBy } }
      syncedInteractiveStates: {},
      interactivePollingInterval: null,
      // Track local interactions for conflict resolution
      localInteractionTimestamps: {}, // { elementId: timestamp }

      // Video playback state (teacher controls, students mirror)
      // { [elementId]: { isPlaying, currentTime, muted, updatedAt, anchorAt } }
      syncedVideoStates: {},
      videoStatePollingInterval: null,

      // Teacher pointer state (teacher broadcasts, students mirror)
      // x/y are 0..100 percentages of the board so they map across viewport sizes
      syncedPointerState: { active: false, x: null, y: null, slideIndex: null, updatedAt: 0 },
      pointerStatePollingInterval: null,

      // Drawing strokes state (synced between teacher and student)
      // Array of strokes for the current slide: [{id, points, color, size, userId, timestamp}]
      drawingStrokes: [],
      drawingPollingInterval: null,
      lastDrawingFetchTime: 0,
      // Active drawing tool state
      drawingTool: null, // 'pencil' | 'eraser' | null
      drawingColor: '#000000',
      drawingSize: 3,

      setBlurEnabled: (enabled) => set({ blurEnabled: enabled }),

      canEnterLesson: (lesson) => {
        const now = get().currentTime;
        const lessonStart = parseAsUTC(lesson.dateTime);
        const minutesBefore = lesson.sourceType === 'club' ? 10 : 5;
        const earlyEntry = new Date(lessonStart.getTime() - minutesBefore * 60000);
        const oneHourAfter = new Date(lessonStart.getTime() + 60 * 60000);
        return now >= earlyEntry && now <= oneHourAfter;
      },

      canPreviewPresentation: (lesson) => {
        const now = get().currentTime;
        const lessonStart = parseAsUTC(lesson.dateTime);
        const twentyFourHoursBefore = new Date(lessonStart.getTime() - 24 * 60 * 60000);
        return now >= twentyFourHoursBefore && now < lessonStart;
      },

      enterLesson: (lesson) => set({
        showCameraPreview: true,
        pendingLesson: lesson,
        initialCameraEnabled: true,
        initialMicEnabled: true,
      }),

      cancelCameraPreview: () => set({ showCameraPreview: false, pendingLesson: null }),

      enterClass: (lesson, { cameraEnabled = true, micEnabled = true } = {}) => set({
        showCameraPreview: false,
        pendingLesson: null,
        currentLessonForClass: lesson,
        inClass: true,
        initialCameraEnabled: cameraEnabled,
        initialMicEnabled: micEnabled,
      }),

      endClass: () => {
        const timerInterval = get().timerPollingInterval;
        const presentationInterval = get().presentationPollingInterval;
        const presenceInterval = get().presencePollingInterval;
        const answersInterval = get().answersPollingInterval;
        const teacherAnswersInterval = get().teacherAnswersPollingInterval;
        const positionsInterval = get().positionsPollingInterval;
        const interactiveInterval = get().interactivePollingInterval;
        if (timerInterval) clearInterval(timerInterval);
        if (presentationInterval) clearInterval(presentationInterval);
        if (presenceInterval) clearInterval(presenceInterval);
        if (answersInterval) clearInterval(answersInterval);
        if (teacherAnswersInterval) clearInterval(teacherAnswersInterval);
        if (positionsInterval) clearInterval(positionsInterval);
        if (interactiveInterval) clearInterval(interactiveInterval);
        const drawingInterval = get().drawingPollingInterval;
        if (drawingInterval) clearInterval(drawingInterval);
        const videoStateInterval = get().videoStatePollingInterval;
        if (videoStateInterval) clearInterval(videoStateInterval);
        const pointerInterval = get().pointerStatePollingInterval;
        if (pointerInterval) clearInterval(pointerInterval);
        set({
          inClass: false,
          currentLessonForClass: null,
          autoEndWarningActive: false,
          autoEndRemainingSec: 0,
          timerActive: false,
          timerEndTime: null,
          timerDuration: 0,
          timerRemainingSeconds: 0,
          timerJustEnded: false,
          timerPollingInterval: null,
          _timerAnchorPerfMs: 0,
          _timerAnchorRemainingSec: 0,
          presentationShared: false,
          syncedSlideIndex: 0,
          syncedPresentationId: null,
          presentationPollingInterval: null,
          participants: [],
          participantsLoading: false,
          presencePollingInterval: null,
          studentAnswersMap: {},
          answersPollingInterval: null,
          teacherAnswersMap: {},
          teacherAnswersPollingInterval: null,
          syncedElementPositions: {},
          positionsPollingInterval: null,
          localDragTimestamps: {},
          syncedInteractiveStates: {},
          interactivePollingInterval: null,
          localInteractionTimestamps: {},
          drawingStrokes: [],
          drawingPollingInterval: null,
          lastDrawingFetchTime: 0,
          drawingTool: null,
          drawingColor: '#000000',
          drawingSize: 3,
          syncedVideoStates: {},
          videoStatePollingInterval: null,
          syncedPointerState: { active: false, x: null, y: null, slideIndex: null, updatedAt: 0 },
          pointerStatePollingInterval: null
        });
      },

      tick: () => set({ currentTime: new Date() }),

      // Auto-end countdown. Called every second by VirtualClassroom while inClass.
      // Returns { shouldEnd } so the caller can trigger a single end-flow when the
      // hard cutoff (scheduled_end + 10 min) is crossed. Warning modal (5-min
      // countdown) shows between +5 and +10 min past scheduled_end.
      updateAutoEndCountdown: () => {
        const lesson = get().currentLessonForClass;
        if (!lesson || !lesson.dateTime) return { shouldEnd: false };
        const parsedStart = parseAsUTC(lesson.dateTime);
        const startMs = parsedStart ? parsedStart.getTime() : null;
        if (!startMs || Number.isNaN(startMs)) return { shouldEnd: false };
        const durationMin = lesson.duration || 60;
        const scheduledEndMs = startMs + durationMin * 60_000;
        const warningStartMs = scheduledEndMs + 5 * 60_000;
        const autoEndMs = scheduledEndMs + 10 * 60_000;
        const now = Date.now();

        if (now >= autoEndMs) {
          if (get().autoEndWarningActive || get().autoEndRemainingSec > 0) {
            set({ autoEndWarningActive: false, autoEndRemainingSec: 0 });
          }
          return { shouldEnd: true };
        }

        if (now >= warningStartMs) {
          const remaining = Math.max(0, Math.ceil((autoEndMs - now) / 1000));
          const state = get();
          if (!state.autoEndWarningActive || state.autoEndRemainingSec !== remaining) {
            set({ autoEndWarningActive: true, autoEndRemainingSec: remaining });
          }
          return { shouldEnd: false };
        }

        if (get().autoEndWarningActive) {
          set({ autoEndWarningActive: false, autoEndRemainingSec: 0 });
        }
        return { shouldEnd: false };
      },

      // Timer actions
      fetchTimer: async (lessonId) => {
        // Snapshot before the request so a stale "no timer" response that
        // arrives after a local start doesn't clobber the fresh state.
        const startedAtBeforeFetch = get()._timerLastStartedAt;
        try {
          const res = await fetch(`/api/timer/${lessonId}`, { credentials: 'include' });
          // Bail on 401/500/etc — the JSON body has no `active` field, and
          // falling through to the else-branch would wipe an active timer.
          if (!res.ok) return;
          const data = await res.json();

          if (data.justEnded) {
            set({ timerJustEnded: true, timerActive: false });
            return;
          }

          if (data.active) {
            // Re-anchor to server truth ONLY when there's meaningful drift
            // (>2s) or when this is the first authoritative reading. Otherwise
            // keep the existing anchor so the display doesn't jitter by ±1s
            // every poll from routine network RTT (Math.ceil rounding at the
            // second boundary).
            const {
              _timerAnchorPerfMs: prevPerf,
              _timerAnchorRemainingSec: prevRem,
              timerActive: wasActive,
            } = get();
            const nowPerf = performance.now();
            const localRemaining = wasActive && prevPerf
              ? Math.ceil(prevRem - (nowPerf - prevPerf) / 1000)
              : null;
            const shouldReanchor =
              !wasActive ||
              localRemaining === null ||
              Math.abs(localRemaining - data.remainingSeconds) > 2;
            set({
              timerActive: true,
              timerEndTime: data.endTime,
              timerDuration: data.duration,
              timerJustEnded: false,
              ...(shouldReanchor && {
                timerRemainingSeconds: data.remainingSeconds,
                _timerAnchorPerfMs: nowPerf,
                _timerAnchorRemainingSec: data.remainingSeconds,
              }),
            });
          } else {
            // Race guard: a GET fired before the local POST /start reached
            // the server can reply "no timer" after the POST already flipped
            // us active. Ignore that reply.
            if (get()._timerLastStartedAt !== startedAtBeforeFetch) return;
            set({
              timerActive: false,
              timerEndTime: null,
              timerDuration: 0,
              timerRemainingSeconds: 0,
              _timerAnchorPerfMs: 0,
              _timerAnchorRemainingSec: 0,
            });
          }
        } catch (error) {
          console.error('Failed to fetch timer:', error);
        }
      },

      startTimer: async (lessonId, duration, teacherId) => {
        try {
          const res = await fetch(`/api/timer/${lessonId}/start`, {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ duration, teacherId })
          });
          const data = await res.json();

          if (data.success) {
            set({
              timerActive: true,
              timerEndTime: data.endTime,
              timerDuration: data.duration,
              timerRemainingSeconds: data.remainingSeconds,
              timerJustEnded: false,
              _timerLastStartedAt: Date.now(),
              _timerAnchorPerfMs: performance.now(),
              _timerAnchorRemainingSec: data.remainingSeconds,
            });
          }
          return data;
        } catch (error) {
          console.error('Failed to start timer:', error);
          return { success: false, error };
        }
      },

      stopTimer: async (lessonId) => {
        try {
          await fetch(`/api/timer/${lessonId}/stop`, {
        credentials: 'include', method: 'POST' });
          set({
            timerActive: false,
            timerEndTime: null,
            timerDuration: 0,
            timerRemainingSeconds: 0,
            timerJustEnded: false,
            _timerAnchorPerfMs: 0,
            _timerAnchorRemainingSec: 0,
          });
        } catch (error) {
          console.error('Failed to stop timer:', error);
        }
      },

      updateTimerLocally: () => {
        const { timerActive, _timerAnchorPerfMs, _timerAnchorRemainingSec } = get();
        if (!timerActive || !_timerAnchorPerfMs) return;

        // Use performance.now() (monotonic — immune to system clock changes
        // like NTP adjustments or a manually-wrong clock on the teacher's Mac)
        // instead of Date.now(). Previously the countdown was derived from
        // `endTime − Date.now()` where endTime was a server-side wall-clock
        // timestamp; any skew between the client clock and the server clock
        // (or an NTP correction mid-lesson) caused the timer to display the
        // wrong value on start, then jump back and forth every 3s as the
        // server poll re-synced.
        const elapsedSec = (performance.now() - _timerAnchorPerfMs) / 1000;
        const remaining = Math.ceil(_timerAnchorRemainingSec - elapsedSec);
        if (remaining <= 0) {
          set({
            timerActive: false,
            timerRemainingSeconds: 0,
            timerJustEnded: true,
            _timerAnchorPerfMs: 0,
            _timerAnchorRemainingSec: 0,
          });
        } else {
          set({ timerRemainingSeconds: remaining });
        }
      },

      clearTimerJustEnded: () => set({ timerJustEnded: false }),

      startTimerPolling: (lessonId) => {
        const existingInterval = get().timerPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchTimer(lessonId);

        // Poll every 3 seconds for sync
        const interval = setInterval(() => {
          get().fetchTimer(lessonId);
        }, 3000);

        set({ timerPollingInterval: interval });
      },

      stopTimerPolling: () => {
        const interval = get().timerPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ timerPollingInterval: null });
        }
      },

      // Presentation state actions
      fetchPresentationState: async (lessonId) => {
        try {
          const res = await fetch(`${API.PRESENTATION_STATE}/${lessonId}`);
          const data = await res.json();
          set({
            presentationShared: data.shared || false,
            syncedSlideIndex: data.slideIndex || 0,
            syncedPresentationId: data.presentationId || null
          });
        } catch (error) {
          console.error('Failed to fetch presentation state:', error);
        }
      },

      updatePresentationState: async (lessonId, { shared, slideIndex, teacherId, presentationId }) => {
        try {
          const res = await fetch(`${API.PRESENTATION_STATE}/${lessonId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shared, slideIndex, teacherId, presentationId })
          });
          const data = await res.json();
          if (data.success) {
            set({
              presentationShared: data.shared,
              syncedSlideIndex: data.slideIndex,
              syncedPresentationId: data.presentationId
            });
          }
          return data;
        } catch (error) {
          console.error('Failed to update presentation state:', error);
          return { success: false, error };
        }
      },

      startPresentationPolling: (lessonId) => {
        const existingInterval = get().presentationPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchPresentationState(lessonId);

        // Poll every 1 second for smooth slide sync
        const interval = setInterval(() => {
          get().fetchPresentationState(lessonId);
        }, 1000);

        set({ presentationPollingInterval: interval });
      },

      stopPresentationPolling: () => {
        const interval = get().presentationPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ presentationPollingInterval: null });
        }
      },

      // Participant actions (legacy - fetches all booked, not just online)
      fetchClubParticipants: async (clubId) => {
        set({ participantsLoading: true });
        try {
          const res = await fetch(`/api/clubs/${clubId}/participants`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            set({ participants: data.participants || [], participantsLoading: false });
          } else {
            set({ participantsLoading: false });
          }
        } catch (error) {
          console.error('Failed to fetch club participants:', error);
          set({ participantsLoading: false });
        }
      },

      // Presence tracking - tracks who is actually online in the lesson
      sendPresenceHeartbeat: async (lessonSessionId, participant) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/presence`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              participantId: participant.id,
              name: participant.name,
              avatarUrl: participant.avatarUrl,
              role: participant.role
            })
          });
        } catch (error) {
          console.error('Failed to send presence heartbeat:', error);
        }
      },

      fetchOnlineParticipants: async (lessonSessionId) => {
        try {
          const res = await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/presence`);
          const data = await res.json();
          if (res.ok) {
            set({ participants: data.participants || [] });
          }
        } catch (error) {
          console.error('Failed to fetch online participants:', error);
        }
      },

      startPresencePolling: (lessonSessionId, currentParticipant) => {
        const existingInterval = get().presencePollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Send initial heartbeat
        get().sendPresenceHeartbeat(lessonSessionId, currentParticipant);
        get().fetchOnlineParticipants(lessonSessionId);

        // Poll every 3 seconds
        const interval = setInterval(() => {
          get().sendPresenceHeartbeat(lessonSessionId, currentParticipant);
          get().fetchOnlineParticipants(lessonSessionId);
        }, 3000);

        set({ presencePollingInterval: interval });
      },

      stopPresencePolling: async (lessonSessionId, participantId) => {
        const interval = get().presencePollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ presencePollingInterval: null });
        }

        // Notify server that participant left. `keepalive: true` lets the request
        // finish even if the tab is closing / navigating away — without it the
        // browser cancels the fetch and the 'left' event never gets logged.
        if (lessonSessionId && participantId) {
          try {
            await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/presence/${participantId}`, {
              method: 'DELETE',
              keepalive: true
            });
          } catch (error) {
            console.error('Failed to send leave notification:', error);
          }
        }
      },

      clearParticipants: () => set({ participants: [], participantsLoading: false }),

      // Student answers sync - students send their answers
      sendStudentAnswers: async (lessonSessionId, participant, answers, slideIndex) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/answers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              participantId: participant.id,
              studentName: participant.name,
              answers,
              slideIndex
            })
          });
        } catch (error) {
          console.error('Failed to send student answers:', error);
        }
      },

      // Teacher fetches all student answers
      fetchStudentAnswers: async (lessonSessionId, slideIndex) => {
        try {
          const url = slideIndex !== undefined
            ? `${API.PRESENTATION_STATE}/${lessonSessionId}/answers?slideIndex=${slideIndex}`
            : `${API.PRESENTATION_STATE}/${lessonSessionId}/answers`;
          const res = await fetch(url);
          const data = await res.json();
          if (res.ok && data.answers) {
            const answersMap = {};
            data.answers.forEach(item => {
              answersMap[item.participantId] = {
                studentName: item.studentName,
                answers: item.answers,
                slideIndex: item.slideIndex,
                updatedAt: item.updatedAt
              };
            });
            set({ studentAnswersMap: answersMap });
          }
        } catch (error) {
          console.error('Failed to fetch student answers:', error);
        }
      },

      // Teacher starts polling for student answers
      startAnswersPolling: (lessonSessionId, slideIndex) => {
        const existingInterval = get().answersPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchStudentAnswers(lessonSessionId, slideIndex);

        // Poll every 1 second for real-time updates
        const interval = setInterval(() => {
          get().fetchStudentAnswers(lessonSessionId, slideIndex);
        }, 1000);

        set({ answersPollingInterval: interval });
      },

      stopAnswersPolling: () => {
        const interval = get().answersPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ answersPollingInterval: null });
        }
      },

      clearStudentAnswers: () => set({ studentAnswersMap: {} }),

      // Teacher fetches student exercise answers (quiz selections and correctness)
      fetchExerciseAnswers: async (lessonSessionId, slideIndex) => {
        try {
          const url = slideIndex !== undefined
            ? `${API.PRESENTATION_STATE}/${lessonSessionId}/exercise-answers?slideIndex=${slideIndex}`
            : `${API.PRESENTATION_STATE}/${lessonSessionId}/exercise-answers`;
          const res = await fetch(url);
          const data = await res.json();
          if (res.ok) {
            set({ studentExerciseAnswersMap: data.answers || {} });
          }
        } catch (error) {
          console.error('Failed to fetch exercise answers:', error);
        }
      },

      // Teacher starts polling for student exercise answers
      startExerciseAnswersPolling: (lessonSessionId, slideIndex) => {
        const existingInterval = get().exerciseAnswersPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchExerciseAnswers(lessonSessionId, slideIndex);

        // Poll every 1 second for real-time updates
        const interval = setInterval(() => {
          get().fetchExerciseAnswers(lessonSessionId, slideIndex);
        }, 1000);

        set({ exerciseAnswersPollingInterval: interval });
      },

      stopExerciseAnswersPolling: () => {
        const interval = get().exerciseAnswersPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ exerciseAnswersPollingInterval: null });
        }
      },

      clearExerciseAnswers: () => set({ studentExerciseAnswersMap: {} }),

      // Teacher answers sync - teacher sends their answers (for editable elements)
      sendTeacherAnswers: async (lessonSessionId, answers, slideIndex) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/teacher-answers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              answers,
              slideIndex
            })
          });
        } catch (error) {
          console.error('Failed to send teacher answers:', error);
        }
      },

      // Students fetch teacher answers
      fetchTeacherAnswers: async (lessonSessionId, slideIndex) => {
        try {
          const url = slideIndex !== undefined
            ? `${API.PRESENTATION_STATE}/${lessonSessionId}/teacher-answers?slideIndex=${slideIndex}`
            : `${API.PRESENTATION_STATE}/${lessonSessionId}/teacher-answers`;
          const res = await fetch(url);
          const data = await res.json();
          if (res.ok && data.answers) {
            set({
              teacherAnswersMap: {
                answers: data.answers,
                slideIndex: data.slideIndex,
                updatedAt: data.updatedAt
              }
            });
          }
        } catch (error) {
          console.error('Failed to fetch teacher answers:', error);
        }
      },

      // Students start polling for teacher answers
      startTeacherAnswersPolling: (lessonSessionId, slideIndex) => {
        const existingInterval = get().teacherAnswersPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchTeacherAnswers(lessonSessionId, slideIndex);

        // Poll every 1 second for real-time updates
        const interval = setInterval(() => {
          get().fetchTeacherAnswers(lessonSessionId, slideIndex);
        }, 1000);

        set({ teacherAnswersPollingInterval: interval });
      },

      stopTeacherAnswersPolling: () => {
        const interval = get().teacherAnswersPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ teacherAnswersPollingInterval: null });
        }
      },

      clearTeacherAnswers: () => set({ teacherAnswersMap: {} }),

      // Element positions sync - send positions when dragging
      sendElementPositions: async (lessonSessionId, positions, slideIndex, participantId) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/positions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              positions,
              slideIndex,
              participantId
            })
          });
        } catch (error) {
          console.error('Failed to send element positions:', error);
        }
      },

      // Fetch element positions with conflict resolution
      // Accepts server updates for elements that were updated by other participants
      fetchElementPositions: async (lessonSessionId, slideIndex, currentUserId) => {
        try {
          const url = slideIndex !== undefined
            ? `${API.PRESENTATION_STATE}/${lessonSessionId}/positions?slideIndex=${slideIndex}`
            : `${API.PRESENTATION_STATE}/${lessonSessionId}/positions`;
          const res = await fetch(url);
          const data = await res.json();
          if (res.ok && data.positions && Object.keys(data.positions).length > 0) {
            const { localDragTimestamps, syncedElementPositions } = get();
            const newSyncedPositions = { ...syncedElementPositions };

            // Merge server positions with local state
            for (const [elementId, serverPos] of Object.entries(data.positions)) {
              const localDragTime = localDragTimestamps[elementId] || 0;
              const serverUpdatedAt = serverPos.updatedAt || 0;
              const serverUpdatedBy = serverPos.updatedBy;

              // Accept server update if:
              // 1. The server position was updated by someone else
              // 2. OR the server update is newer than our local drag
              // 3. OR we haven't dragged this element locally
              const updatedByOther = serverUpdatedBy && String(serverUpdatedBy) !== String(currentUserId);
              const serverIsNewer = serverUpdatedAt > localDragTime;
              const notDraggedLocally = localDragTime === 0;

              if (updatedByOther || serverIsNewer || notDraggedLocally) {
                newSyncedPositions[elementId] = {
                  x: serverPos.x,
                  y: serverPos.y,
                  updatedAt: serverUpdatedAt,
                  updatedBy: serverUpdatedBy
                };
              }
            }

            set({ syncedElementPositions: newSyncedPositions });
          }
        } catch (error) {
          console.error('Failed to fetch element positions:', error);
        }
      },

      // Track a local drag for conflict resolution
      trackLocalDrag: (elementId) => {
        set(state => ({
          localDragTimestamps: {
            ...state.localDragTimestamps,
            [elementId]: Date.now()
          }
        }));
      },

      // Clear local drag timestamp for an element (e.g., after a short delay)
      clearLocalDragTimestamp: (elementId) => {
        set(state => {
          const newTimestamps = { ...state.localDragTimestamps };
          delete newTimestamps[elementId];
          return { localDragTimestamps: newTimestamps };
        });
      },

      // Start polling for element positions
      startPositionsPolling: (lessonSessionId, slideIndex, currentUserId) => {
        const existingInterval = get().positionsPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchElementPositions(lessonSessionId, slideIndex, currentUserId);

        // Poll every 500ms for smooth position updates
        const interval = setInterval(() => {
          get().fetchElementPositions(lessonSessionId, slideIndex, currentUserId);
        }, 500);

        set({ positionsPollingInterval: interval });
      },

      stopPositionsPolling: () => {
        const interval = get().positionsPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ positionsPollingInterval: null });
        }
      },

      clearElementPositions: () => set({ syncedElementPositions: {} }),

      // Optimistically write an interactive state locally before the server roundtrip.
      // Used for immediate visual feedback (e.g. highlight glow toggle) — the next
      // poll will reconcile via conflict resolution in fetchInteractiveStates.
      setLocalInteractiveState: (elementId, elementType, state, participantId) => {
        const now = Date.now();
        set(s => ({
          syncedInteractiveStates: {
            ...s.syncedInteractiveStates,
            [elementId]: { elementType, state, updatedAt: now, updatedBy: participantId }
          },
          localInteractionTimestamps: {
            ...s.localInteractionTimestamps,
            [elementId]: now
          }
        }));
      },

      // Interactive elements state sync - send state when user interacts
      sendInteractiveState: async (lessonSessionId, elementId, elementType, state, slideIndex, participantId) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/interactive-state`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              elementId,
              elementType,
              state,
              slideIndex,
              participantId
            })
          });
        } catch (error) {
          console.error('Failed to send interactive state:', error);
        }
      },

      // Fetch interactive states with conflict resolution
      fetchInteractiveStates: async (lessonSessionId, slideIndex, currentUserId) => {
        try {
          const url = slideIndex !== undefined
            ? `${API.PRESENTATION_STATE}/${lessonSessionId}/interactive-state?slideIndex=${slideIndex}`
            : `${API.PRESENTATION_STATE}/${lessonSessionId}/interactive-state`;
          const res = await fetch(url);
          const data = await res.json();
          if (res.ok && data.states && Object.keys(data.states).length > 0) {
            const { localInteractionTimestamps, syncedInteractiveStates } = get();
            const newSyncedStates = { ...syncedInteractiveStates };

            // Convert server-clock `startedAt` timestamps into this client's
            // local clock so timers (boggle, word-pool) show the same elapsed
            // time on teacher and student even when their device clocks drift.
            const serverTime = typeof data.serverTime === 'number' ? data.serverTime : null;
            const serverTimeOffset = serverTime !== null ? serverTime - Date.now() : 0;

            // Merge server states with local state
            for (const [elementId, serverState] of Object.entries(data.states)) {
              const localInteractionTime = localInteractionTimestamps[elementId] || 0;
              const serverUpdatedAt = serverState.updatedAt || 0;
              const serverUpdatedBy = serverState.updatedBy;

              // Accept server update if:
              // 1. Updated by someone else
              // 2. Server update is newer than local interaction
              // 3. We haven't interacted with this element locally
              const updatedByOther = serverUpdatedBy && String(serverUpdatedBy) !== String(currentUserId);
              const serverIsNewer = serverUpdatedAt > localInteractionTime;
              const notInteractedLocally = localInteractionTime === 0;

              if (updatedByOther || serverIsNewer || notInteractedLocally) {
                let stateForStore = serverState.state;
                if (
                  stateForStore &&
                  typeof stateForStore === 'object' &&
                  typeof stateForStore.startedAt === 'number' &&
                  stateForStore.startedAt > 0
                ) {
                  stateForStore = {
                    ...stateForStore,
                    startedAt: stateForStore.startedAt - serverTimeOffset
                  };
                }
                newSyncedStates[elementId] = {
                  elementType: serverState.elementType,
                  state: stateForStore,
                  updatedAt: serverUpdatedAt,
                  updatedBy: serverUpdatedBy
                };
              }
            }

            set({ syncedInteractiveStates: newSyncedStates });
          }
        } catch (error) {
          console.error('Failed to fetch interactive states:', error);
        }
      },

      // Track a local interaction for conflict resolution
      trackLocalInteraction: (elementId) => {
        set(state => ({
          localInteractionTimestamps: {
            ...state.localInteractionTimestamps,
            [elementId]: Date.now()
          }
        }));
      },

      // Clear local interaction timestamp
      clearLocalInteractionTimestamp: (elementId) => {
        set(state => {
          const newTimestamps = { ...state.localInteractionTimestamps };
          delete newTimestamps[elementId];
          return { localInteractionTimestamps: newTimestamps };
        });
      },

      // Start polling for interactive states
      startInteractivePolling: (lessonSessionId, slideIndex, currentUserId) => {
        const existingInterval = get().interactivePollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Initial fetch
        get().fetchInteractiveStates(lessonSessionId, slideIndex, currentUserId);

        // Poll every 500ms for smooth updates
        const interval = setInterval(() => {
          get().fetchInteractiveStates(lessonSessionId, slideIndex, currentUserId);
        }, 500);

        set({ interactivePollingInterval: interval });
      },

      stopInteractivePolling: () => {
        const interval = get().interactivePollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ interactivePollingInterval: null });
        }
      },

      clearInteractiveStates: () => set({ syncedInteractiveStates: {}, localInteractionTimestamps: {} }),

      // ============ DRAWING STROKES SYNC ============

      // Set active drawing tool
      setDrawingTool: (tool) => set({ drawingTool: tool }),
      setDrawingColor: (color) => set({ drawingColor: color }),
      setDrawingSize: (size) => set({ drawingSize: size }),

      // Add a local stroke (for immediate feedback)
      addLocalStroke: (stroke) => {
        set(state => ({
          drawingStrokes: [...state.drawingStrokes, stroke]
        }));
      },

      // Send drawing stroke to server
      sendDrawingStroke: async (lessonSessionId, stroke, slideIndex, participantId) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/drawing`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              stroke,
              slideIndex,
              participantId
            })
          });
        } catch (error) {
          console.error('Failed to send drawing stroke:', error);
        }
      },

      // Fetch drawing strokes with incremental updates
      fetchDrawingStrokes: async (lessonSessionId, slideIndex) => {
        try {
          const { lastDrawingFetchTime, drawingStrokes } = get();
          const url = `${API.PRESENTATION_STATE}/${lessonSessionId}/drawing?slideIndex=${slideIndex}&since=${lastDrawingFetchTime}`;
          const res = await fetch(url);
          const data = await res.json();

          if (res.ok && data.strokes) {
            if (lastDrawingFetchTime === 0) {
              // First fetch - replace all strokes
              set({
                drawingStrokes: data.strokes,
                lastDrawingFetchTime: data.updatedAt || Date.now()
              });
            } else if (data.strokes.length > 0) {
              // Incremental update - add new strokes that don't exist locally
              const existingIds = new Set(drawingStrokes.map(s => s.id));
              const newStrokes = data.strokes.filter(s => !existingIds.has(s.id));
              if (newStrokes.length > 0) {
                set(state => ({
                  drawingStrokes: [...state.drawingStrokes, ...newStrokes],
                  lastDrawingFetchTime: data.updatedAt || Date.now()
                }));
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch drawing strokes:', error);
        }
      },

      // Erase strokes
      eraseStrokes: async (lessonSessionId, strokeIds, slideIndex) => {
        // Remove locally first for immediate feedback
        set(state => ({
          drawingStrokes: state.drawingStrokes.filter(s => !strokeIds.includes(s.id))
        }));

        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/drawing`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              strokeIds,
              slideIndex
            })
          });
        } catch (error) {
          console.error('Failed to erase strokes:', error);
        }
      },

      // Clear all strokes on a slide
      clearAllStrokes: async (lessonSessionId, slideIndex) => {
        set({ drawingStrokes: [] });

        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/drawing`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              clearAll: true,
              slideIndex
            })
          });
        } catch (error) {
          console.error('Failed to clear all strokes:', error);
        }
      },

      // Start polling for drawing strokes
      startDrawingPolling: (lessonSessionId, slideIndex) => {
        const existingInterval = get().drawingPollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        // Reset state for new slide
        set({ drawingStrokes: [], lastDrawingFetchTime: 0 });

        // Initial fetch
        get().fetchDrawingStrokes(lessonSessionId, slideIndex);

        // Poll every 300ms for smooth drawing sync
        const interval = setInterval(() => {
          get().fetchDrawingStrokes(lessonSessionId, slideIndex);
        }, 300);

        set({ drawingPollingInterval: interval });
      },

      stopDrawingPolling: () => {
        const interval = get().drawingPollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ drawingPollingInterval: null });
        }
      },

      // Clear drawing strokes (e.g., when changing slides)
      clearDrawingStrokes: () => set({ drawingStrokes: [], lastDrawingFetchTime: 0 }),

      // ============ VIDEO PLAYBACK SYNC ============

      // Teacher pushes play/pause/seek/mute state for a video element
      sendVideoState: async (lessonSessionId, elementId, { isPlaying, currentTime, muted }) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/video-state`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ elementId, isPlaying, currentTime, muted })
          });
        } catch (error) {
          console.error('Failed to send video state:', error);
        }
      },

      fetchVideoStates: async (lessonSessionId) => {
        try {
          const res = await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/video-state`);
          const data = await res.json();
          if (res.ok && data.states) {
            set({ syncedVideoStates: data.states });
          }
        } catch (error) {
          console.error('Failed to fetch video states:', error);
        }
      },

      startVideoStatePolling: (lessonSessionId) => {
        const existingInterval = get().videoStatePollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        get().fetchVideoStates(lessonSessionId);

        const interval = setInterval(() => {
          get().fetchVideoStates(lessonSessionId);
        }, 500);

        set({ videoStatePollingInterval: interval });
      },

      stopVideoStatePolling: () => {
        const interval = get().videoStatePollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ videoStatePollingInterval: null });
        }
      },

      // ============ TEACHER POINTER SYNC ============

      // Teacher broadcasts pointer state. x/y are 0..100 percentages of the board.
      sendPointerState: async (lessonSessionId, { active, x, y, slideIndex, teacherId }) => {
        try {
          await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/pointer-state`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ active, x, y, slideIndex, teacherId })
          });
        } catch (error) {
          console.error('Failed to send pointer state:', error);
        }
      },

      fetchPointerState: async (lessonSessionId) => {
        try {
          const res = await fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/pointer-state`);
          const data = await res.json();
          if (res.ok && data) {
            set({
              syncedPointerState: {
                active: !!data.active,
                x: typeof data.x === 'number' ? data.x : null,
                y: typeof data.y === 'number' ? data.y : null,
                slideIndex: typeof data.slideIndex === 'number' ? data.slideIndex : null,
                updatedAt: data.updatedAt || 0
              }
            });
          }
        } catch (error) {
          console.error('Failed to fetch pointer state:', error);
        }
      },

      startPointerStatePolling: (lessonSessionId) => {
        const existingInterval = get().pointerStatePollingInterval;
        if (existingInterval) clearInterval(existingInterval);

        get().fetchPointerState(lessonSessionId);

        // 200ms gives a smooth-enough cursor without overwhelming the server
        const interval = setInterval(() => {
          get().fetchPointerState(lessonSessionId);
        }, 200);

        set({ pointerStatePollingInterval: interval });
      },

      stopPointerStatePolling: () => {
        const interval = get().pointerStatePollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ pointerStatePollingInterval: null });
        }
      },
    }),
    {
      name: 'lingvish_classroom',
      // Persist just enough to survive a page reload during a lesson. Without
      // this, `inClass` resets to false and AppLayout drops the user back onto
      // the dashboard route mid-class.
      partialize: (state) => ({
        blurEnabled: state.blurEnabled,
        inClass: state.inClass,
        currentLessonForClass: state.currentLessonForClass,
        initialCameraEnabled: state.initialCameraEnabled,
        initialMicEnabled: state.initialMicEnabled,
      }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted || {}) };
        // Drop a stale `inClass` flag if we're past the lesson's live window,
        // so an old session doesn't yank a returning user back into a finished
        // class. Two hours after start covers regular reload + a bit of slack.
        if (merged.inClass && merged.currentLessonForClass?.dateTime) {
          const startMs = parseAsUTC(merged.currentLessonForClass.dateTime)?.getTime();
          if (!startMs || Number.isNaN(startMs) || Date.now() > startMs + 2 * 60 * 60 * 1000) {
            merged.inClass = false;
            merged.currentLessonForClass = null;
          }
        } else if (merged.inClass && !merged.currentLessonForClass) {
          merged.inClass = false;
        }
        return merged;
      },
    }
  )
);

export default useClassroomStore;
